<script setup lang="ts">
/**
 * VITRINE d'une boutique — `/boutique/[slug]`.
 *
 * ⚠️ CETTE PAGE NE PORTE PLUS LE TUNNEL DE COMMANDE (chantier « panier »).
 *
 * Elle hébergeait auparavant les trois étapes (`checkoutStep` 1/2/3 : panier →
 * coordonnées → récapitulatif). Comme la fiche produit est une page à part,
 * l'acheteur qui ajoutait un article depuis celle-ci était renvoyé ICI et
 * devait faire défiler jusqu'au bloc panier, tout en bas de l'étape 1 — aucun
 * accès permanent au panier, aucune URL de commande.
 *
 * Répartition désormais :
 *  - la VITRINE (ce fichier) = catalogue seul ;
 *  - le PANNEAU LATÉRAL (`BoutiqueCartDrawer`) = le panier, accessible en
 *    permanence via `BoutiqueCartButton` et ouvert automatiquement à l'ajout ;
 *  - la PAGE PANIER (`panier.vue`) = la commande, avec toute la logique du
 *    tunnel DÉPLACÉE (et non recopiée) dans `useShopCheckoutTunnel`.
 *
 * Le panier survit à toutes ces navigations : `useShopCart` le persiste en
 * sessionStorage par boutique (`shopcart:{slug}`).
 */
import { shopProductPath } from "~/utils/shopProductUrl";
import type { Event } from "~/types/event";
import type { ShopProduct } from "~/types/shop";
import { useChatPageContext, euros, dateLisible, phrasePaysAssociation } from "~/composables/useChatPageContext";
import { stripHtml } from "@donoor/utils/string";
import { useDemoAccueil } from "~/composables/useDemoAccueil";

const { t } = useI18n();

const { sanitize } = useSanitizeHtml();

definePageMeta({ layout: false });

const route = useRoute();
// Démo de la home (useDemoAccueil) : la page y est affichée telle quelle, son
// slug vient de la démo (l'adresse est celle de la home).
const demoAccueil = useDemoAccueil();
const slug = computed(
  () =>
    demoAccueil.integree?.slug ??
    ((route.params as Record<string, string | string[] | undefined>).slug as string),
);
const config = useRuntimeConfig();

// Mode widget embarqué (?embed=1) : masque l'habillage (banner/header/
// sidebar/footer), fond transparent, auto-resize vers l'hôte.
const embedRootEl = ref<HTMLElement | null>(null);
const { isEmbed, hostOrigin } = useEmbedFrame(embedRootEl);

// Fetch SSR + disponibilité (404 draft/archived/not_found → vraie raison).
const isPreviewQuery = computed(() => route.query.preview === "true");
const { shop, isInitialLoading, isEntityMissing, missingCopy } =
  await useShopPublicData(slug, isPreviewQuery);

// Vrai statut HTTP 404 côté SSR quand la boutique n'existe pas.
// Jamais dans la démo de la home : c'est la home qui recevrait le 404.
if (import.meta.server && isEntityMissing.value && !demoAccueil.estIntegree) {
  const requestEvent = useRequestEvent();
  if (requestEvent) setResponseStatus(requestEvent, 404);
}

// Contexte donné à l'IA du chat : elle aide en priorité sur CETTE boutique
// tant que le visiteur ne change pas de sujet (Karim 2026-10-12).
const { definirContexte, effacerContexte } = useChatPageContext();
watchEffect(() => {
  const s = shop.value;
  if (!s) return;
  const produits = s.products ?? [];
  const prixMin = produits.length
    ? Math.min(...produits.map((p) => (p.pricing === "flexible" ? (p.min_price ?? p.price) : p.price)))
    : null;
  const vente = dateLisible(s.sale_end_date);
  const phrases = [
    `Boutique « ${s.title} »${s.organization_name ? ` de l'association ${s.organization_name}` : ""}.`,
    produits.length
      ? `${produits.length} produit(s), à partir de ${euros(prixMin)}.`
      : "Aucun produit disponible pour le moment.",
    vente ? `Vente ouverte jusqu'au ${vente}.` : null,
    s.description ? `Description : ${stripHtml(s.description).slice(0, 600)}` : null,
    phrasePaysAssociation(s.country),
  ].filter(Boolean);
  definirContexte("boutique", phrases.join(" "));
});
onUnmounted(() => effacerContexte());

// Accent color (réutilise le composable /b — seule accent_color est lue).
const { accentStyle } = useEventAccentColor(
  shop as unknown as Ref<Event | null>,
);

// SEO + JSON-LD + OG
const { shopCoverImageId } = useShopCheckoutSeo(shop, slug);

// Socle commun aux trois pages boutique : fenêtre de vente, message de
// clôture, query d'habillage à propager (embed / aperçu).
const {
  isPreview,
  carryQuery,
  canBuy: saleWindowOpen,
  saleClosedMessage,
  showRemainingStock,
} = useShopPageContext(shop);

// ─── Panier (partagé avec la fiche produit et la page panier) ──────
const {
  cartLines,
  products,
  totalItems,
  subtotal,
  getMaxForLine,
  setLineQuantity,
  removeLine,
} = useShopCart(slug, shop);

/** Modal de partage (WhatsApp/réseaux) — parité /b (EventShareModal). */
const shareModalOpen = ref(false);

// ─── Paiement configuré ? ──────────────────────────────────────────
// Seule information de paiement encore utile ici : une boutique qui n'encaisse
// pas ne doit pas laisser croire qu'on peut commander. Le reste du tunnel
// (providers, wallets, frais) a suivi la commande sur la page panier.
const { paymentProviders } = usePaymentProvidersState(shop);
const isPaymentConfigured = computed(
  () => paymentProviders.value.accepts_payments === true,
);

/**
 * Commande réellement possible : la fenêtre de vente est ouverte ET
 * l'association peut encaisser. Une boutique sans provider laisse consulter les
 * produits (comme hors fenêtre de vente), mais ne doit surtout pas proposer un
 * bouton « Commander » qui mènerait à un tunnel sans moyen de paiement.
 */
const canBuy = computed(
  () => saleWindowOpen.value && isPaymentConfigured.value,
);

// ─── Temps réel stock (socket, pattern /b) ─────────────────────────
// Room `shop:{id}` : push serveur de remaining/sold_out par variante après
// chaque vente/annulation/expiration/remboursement. Le clamp panier de
// useShopCart réagit tout seul ; le composable ne fait que patcher + avertir.
// `checkoutStep` figé à 1 : la vitrine n'a plus d'étapes, le composable ne s'en
// sert que pour ramener l'acheteur au panier — sans objet ici.
const staticCheckoutStep = ref(1);
useShopSocket({ shop, totalItems, checkoutStep: staticCheckoutStep });
// Dernier achat (moins de 7 jours) annoncé à l'arrivée (Karim 2026-10-10).
useAnnonceDerniereActivite(
  () => shop.value?.derniere_activite,
  (_a, recente) => t(recente ? "commun.enDirect.vientDAcheter" : "commun.enDirect.aAchete"),
  "i-lucide-shopping-bag",
);

// ─── Navigation ────────────────────────────────────────────────────
/**
 * Ouverture d'un produit = VRAIE NAVIGATION vers `/boutique/{slug}/{produit}`.
 *
 * Une association vend d'abord par WhatsApp, Facebook ou newsletter : un
 * produit sans lien partageable est invendable. Le panier survit à
 * l'aller-retour (sessionStorage par boutique, `useShopCart`), et les query
 * d'habillage sont propagées — en mode widget la navigation reste DANS
 * l'iframe.
 */
function openProduct(product: ShopProduct) {
  return navigateTo({
    path: shopProductPath(slug.value, product),
    query: carryQuery.value,
  });
}

/** Page de commande — destination de « Commander » (panneau et barre mobile). */
const cartPageLink = computed(() => ({
  path: `/boutique/${slug.value}/panier`,
  query: carryQuery.value,
}));

/**
 * Passage à la commande depuis la vitrine.
 *
 * Le PRÉ-VOL de disponibilité du stock n'est PAS dupliqué ici : il vit dans le
 * tunnel (`useShopCheckoutTunnel.validateStock`, joué avant le récapitulatif,
 * donc avant toute saisie de carte). On se contente de la garde minimale —
 * panier non vide, vente ouverte, paiement configuré — avant de naviguer.
 */
// Ne renvoie rien : renvoyer `navigateTo(…)` faisait déduire à TypeScript un
// type de retour trop complexe (TS2590).
function goToCart(): void {
  if (totalItems.value === 0 || !canBuy.value) return;
  void navigateTo(cartPageLink.value);
}
</script>

<template>
  <div
    ref="embedRootEl"
    :class="isEmbed ? 'bg-transparent @container' : ((!isInitialLoading && isEntityMissing) ? 'min-h-screen flex flex-col bg-white dark:bg-[#0a0a0a]' : 'min-h-screen flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]')"
    :style="accentStyle"
  >
    <!-- Surveillance : asso suspended/banned -->
    <template v-if="shop && (shop.organization_risk_status === 'suspended' || shop.organization_risk_status === 'banned')">
      <AppHeader v-if="!isEmbed && !demoAccueil.estIntegree" shop-mode />
      <OrgUnavailable
        :risk-status="shop.organization_risk_status"
        :reason="shop.organization_risk_reason"
        :organization-name="shop.organization_name"
      />
    </template>

    <PublicPageLoadingSkeleton v-else-if="isInitialLoading || (!shop && !isEntityMissing)" />

    <PublicPageErrorState
      v-else-if="isEntityMissing"
      :icon="missingCopy.icon"
      :title="missingCopy.title"
      :message="missingCopy.message"
    >
      <template #suggestions>
        <NotFoundSuggestions type="associations" />
      </template>
    </PublicPageErrorState>

    <template v-else-if="shop">
      <AppHeader v-if="!isEmbed && !demoAccueil.estIntegree" :links="navLinks" shop-mode :cart-count="totalItems" />

      <div v-if="isPreview" class="bg-amber-500 text-white text-center text-sm font-medium py-2 px-4">
        {{ $t("boutique.index.modeApercu") }}
      </div>

      <!-- BANNIÈRE — GARDÉE, et c'est un choix.
           Elle est la seule image d'ambiance de la boutique : la retirer aurait
           fait démarrer la vitrine sur du texte nu, alors qu'un marchand ouvre
           toujours sur une image. Elle porte aussi le LOGO de l'association en
           débord, qui est le premier signal de confiance d'une boutique
           associative. Le composant lui-même n'est pas modifié : `BslugBanner`
           est PARTAGÉ avec la billetterie (/b), le toucher déborderait du
           périmètre.
           Ce qu'on change, c'est son ARTICULATION avec la page : le titre n'est
           plus écrit dans une carte flottant sous elle, il est porté par
           l'en-tête de vitrine juste dessous (`BoutiqueShopHero`), sur le fond
           de page — donc lisible quelle que soit la photo envoyée par
           l'association. Écrire le titre PAR-DESSUS l'image aurait exigé un
           voile sombre, c'est-à-dire une couleur semi-transparente, interdite
           (`ui_no_bg_transparency`).
           La bannière règle par ailleurs la question du header : `AppHeader`
           est une pilule FLOTTANTE (`fixed top-5`), et l'image passe dessous
           en pleine largeur — exactement le comportement attendu d'un hero. Le
           `pt-24 md:pt-32` des pages SANS bannière (dont la fiche produit) n'a
           donc pas lieu d'être ici ; la respiration nécessaire est celle qui
           sépare le logo en débord du titre, apportée ci-dessous. -->
      <BslugBanner
        v-if="!isEmbed"
        :cover-image-id="shopCoverImageId"
        :title="shop.title"
        :organization-logo="shop.organization_logo"
        :organization-name="shop.organization_name"
        :api-base-url="config.public.apiBaseUrl as string"
      />

      <!-- ⚠️ REMONTE PAR-DESSUS LA BANNIÈRE (Karim 2026-08-22). `-mt-6` le
           fait chevaucher, `rounded-t-[28px]` donne le bord arrondi qui rend
           la superposition lisible, `z-10` le place au-dessus de la bannière
           en `z-0`.

           DEUX niveaux, et c'est nécessaire : le FOND arrondi doit couvrir
           TOUTE la largeur (sinon la bannière dépasse sur les côtés — Karim
           2026-08-22), tandis que le CONTENU reste borné à 1280px comme le
           reste du site. Un seul div ne peut pas faire les deux.
           En EMBED : rien de tout ça, il n'y a pas de bannière. -->
      <div
        :class="isEmbed
          ? 'w-full'
          : 'relative z-10 grow -mt-[68px] rounded-t-[28px] bg-[#f7f8fa] dark:bg-[#0a0a0a] pt-8'"
      >
      <div
        :class="isEmbed ? 'w-full' : 'w-full max-w-[1280px] mx-auto px-3 md:px-6'"
      >
        <div class="flex flex-col lg:flex-row gap-6 lg:gap-10">

          <!-- COLONNE PRINCIPALE -->
          <div
            class="flex-1 min-w-0"
            :class="isEmbed
              ? 'lg:w-full pt-0'
              : (totalItems > 0 ? 'lg:w-3/4 pt-2 md:pt-4' : 'lg:w-full pt-2 md:pt-4')"
          >
            <!-- EN-TÊTE DE VITRINE — SANS CARTE (cohérence fiche produit :
                 l'information flotte, seule l'action est encartée). Remplace
                 `BoutiqueHeaderCard`, supprimé. -->
            <BoutiqueShopHero
              v-if="!isEmbed"
              :shop="shop"
              :description-html="sanitize(shop.description || '')"
              :product-count="products.length"
              @open-share="shareModalOpen = true"
            />

            <!-- ⚠️ PAS en aperçu (Karim 2026-09-10) : ce bloc remplaçait le
                 tunnel, or c'est lui que l'organisation vient vérifier. Le
                 paiement reste neutralisé en aperçu. -->
            <PublicPagePaymentUnavailable v-if="!isPaymentConfigured && !isPreviewQuery" class="mt-6" />

            <!-- CATALOGUE. Le tunnel a quitté cette page : plus de barre
                 d'étapes, plus de `checkoutStep`. Le composant porte lui-même
                 son titre de section et l'accès permanent au panier — il n'est
                 plus enfermé dans une carte blanche.
                 ⚠️ Le catalogue reste affiché même sans paiement configuré :
                 les produits sont consultables (parité avec la fenêtre de vente
                 fermée), seule la commande est impossible. -->
            <BoutiqueStepCatalogue
              :shop-slug="slug"
              :is-embed="isEmbed"
              :products="products"
              :total-items="totalItems"
              :subtotal="subtotal"
              :show-remaining-stock="showRemainingStock"
              :can-buy="canBuy"
              :sale-closed-message="saleClosedMessage"
              :class="isEmbed ? 'mt-0' : 'mt-8 md:mt-10'"
              @open-product="openProduct"
              @continue="goToCart"
            />

            <PublicPageSecurityBadges v-if="!isEmbed" class="mt-10" />
            <!-- Embed : « Propulsé par Donoor.org » en bas, centré (Karim 31/08). -->
            <div v-if="isEmbed" class="mt-10 flex justify-center">
              <EmbedPoweredBy type="shop" :host-origin="hostOrigin" />
            </div>
          </div>

          <!-- Colonne de droite affichée SEULEMENT si le panier a des articles
               (Karim 2026-08-09) : vide, elle laissait une bande blanche sur
               tout le flanc droit et rétrécissait la grille pour rien. Le
               partage et le vendeur remontent alors dans l'en-tête de
               catalogue (voir BoutiqueStepCatalogue). -->
          <BoutiqueSidebar
            v-if="!isEmbed && totalItems > 0"
            :shop="shop"
            :checkout-step="1"
            :show-checkout-action="true"
            :can-buy="canBuy"
            :cart-lines="cartLines"
            :total-items="totalItems"
            :subtotal="subtotal"
            :promo-applied="null"
            promo-code=""
            :promo-loading="false"
            promo-error=""
            :promo-discount="0"
            :donation-amount="0"
            :shipping-fee="0"
            :shipping-method-name="null"
            :fees-amount="0"
            :include-fees="false"
            @checkout="goToCart"
          >
            <!-- Panier DANS la carte du récapitulatif, à côté de « Commander ». -->
            <template #cart>
              <BoutiqueCartButton :total-items="totalItems" class="flex-1" />
            </template>
          </BoutiqueSidebar>
        </div>

        <!-- Respiration de bas de page (parité fiche produit). -->
        <div class="pb-6 md:pb-10" />
      </div>
      </div>

      <AppFooter v-if="!isEmbed && !demoAccueil.estIntegree" class="mt-0!" />
    </template>

    <!-- PANNEAU LATÉRAL PANIER — monté au niveau de la PAGE (et non dans le
         catalogue) pour rester au-dessus de tout l'habillage. Même composant,
         même état (`useShopCartDrawer`) et même panier que sur la fiche
         produit : un seul panier, deux points de montage. -->
    <BoutiqueCartDrawer
      :cart-lines="cartLines"
      :total-items="totalItems"
      :subtotal="subtotal"
      :cart-page-link="cartPageLink"
      :can-buy="canBuy"
      :sale-closed-message="saleClosedMessage"
      :get-max-for-line="getMaxForLine"
      @update-quantity="setLineQuantity"
      @remove-line="removeLine"
    />

    <BoutiqueShareModal v-model:open="shareModalOpen" :shop="shop ?? null" />
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
/* Survol en COULEUR PLEINE (règle `ui_no_bg_transparency`). */
.accent-btn:hover {
  background: color-mix(in srgb, var(--accent, #212121) 88%, #000);
}
</style>
